import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form, DatePicker } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import UserSelector from '../../components/UserSelector';
import { useLocation } from 'react-router-dom';

const statusMap = {
    0: { text: '待签署', type: 'default' },
    1: { text: '签署中', type: 'primary' },
    2: { text: '已签署', type: 'success' },
    4: { text: '已到期', type: 'danger' },
};

const typMap = {
    1: '劳务合同',
};

const UserContracts = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);

    // 解析typ参数，默认1
    const searchParams = new URLSearchParams(location.search);
    const urlTyp = parseInt(searchParams.get('typ')) || 1;

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/o2o/list_user_contract/', {
                params: {
                    page: page - 1,
                    typ: urlTyp,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/o2o/add_user_contract/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/o2o/edit_user_contract/', {
                ...values,
                uid: currentRecord.uid,
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/o2o/delete_user_contract/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的合同');
            return;
        }
        try {
            const response = await request.post('/go/sp/o2o/batchedit_user_contract/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '合同名称',
            dataIndex: 'title',
            width: 200,
        },
        {
            title: '用户',
            dataIndex: 'su',
            width: 120,
            render: (su) => su?.name || su?.nickname || su?.uid || '-'
        },
        {
            title: '用户2',
            dataIndex: 'su2',
            width: 120,
            render: (su2) => su2?.name || su2?.nickname || su2?.uid || '-'
        },
        {
            title: '到期时间',
            dataIndex: 'expire_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该合同吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            expire_time: currentRecord.expire_time ? new Date(parseInt(currentRecord.expire_time)) : '',
            content: currentRecord.content || '{}',
            typ: urlTyp,
        } : {
            su_uid: '',
            su2_uid: '',
            typ: urlTyp,
            title: '',
            status: 0,
            expire_time: '',
            content: '{}',
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    const submitValues = {
                        ...values,
                        typ: urlTyp, // 强制typ为urlTyp
                        expire_time: values.expire_time ? (typeof values.expire_time === 'string' ? values.expire_time : values.expire_time.getTime().toString()) : '',
                    };
                    if (isEdit) {
                        handleEdit(submitValues);
                    } else {
                        handleAdd(submitValues);
                    }
                }}
                style={{ padding: '20px' }}
            >
                用户
                <UserSelector
                    value={initialValues.su_uid}
                    onChange={val => formApi && formApi.setValue('su_uid', Array.isArray(val) ? val[0] : val)}
                    placeholder="请选择甲方"
                    multiple={false}
                />

                用户2
                <UserSelector
                    value={initialValues.su2_uid}
                    onChange={val => formApi && formApi.setValue('su2_uid', Array.isArray(val) ? val[0] : val)}
                    placeholder="请选择乙方"
                    multiple={false}
                />
                <Form.Input
                    field="title"
                    label="合同名称"
                    rules={[{ required: true, message: '请输入合同名称' }]}
                />
                <Form.DatePicker
                    field="expire_time"
                    label="到期时间"
                    type="dateTime"
                    rules={[{ required: true, message: '请选择到期时间' }]}
                />
                <Form.Select
                    field="status"
                    label="状态"
                    rules={[{ required: true, message: '请选择状态' }]}
                >
                    <Select.Option value={0}>待签署</Select.Option>
                    <Select.Option value={1}>签署中</Select.Option>
                    <Select.Option value={2}>已签署</Select.Option>
                    <Select.Option value={4}>已到期</Select.Option>
                </Form.Select>
                <Form.TextArea
                    field="content"
                    label="内容(JSON)"
                    placeholder="{file: , records:[]}"
                />
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}>
                <Space>
                    <Input
                        placeholder="搜索合同名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="合同状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增合同</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(4, selectedRowKeys)}>批量到期</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量设为待签署</Button>
                            <Button type='success' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量设为已签署</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个合同吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增合同"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑合同"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default UserContracts; 